import { useState } from "react";
import { advantagesData } from "@/mocks/homepageData";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";

const detailStats = [
  { label: "Regulators", value: "ASIC · CySEC · FSC", sub: "Tier-1 licenses" },
  { label: "Execution Speed", value: "< 1s", sub: "99.35% of orders" },
  { label: "Bonus Available", value: "$30 + $500", sub: "No-deposit + welcome" },
  { label: "Support Hours", value: "24 / 5", sub: "Avg. response < 2 min" },
];

export default function AdvantagesSection() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal(0.1);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <section
      ref={sectionRef}
      className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(sectionVisible, "up")}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center gap-5 mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7C3AED]/40">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED] animate-pulse" />
            <span className="text-sm text-[#F8FAFC]">Why Traders Trust XM</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] max-w-3xl">
            4 Core Advantages That Set{" "}
            <span className="text-gradient">XM Trading</span>
          </h2>
          <p className="text-sm md:text-base text-[#94A3B8] max-w-xl">
            Discover what makes XM the preferred choice for over 10 million traders across 190+ countries worldwide.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {advantagesData.map((item, index) => {
            const isActive = activeIndex === index;
            const stat = detailStats[index];

            return (
              <button
                key={item.title}
                type="button"
                onClick={() => setActiveIndex(isActive ? null : index)}
                className={`group relative text-left bg-[#1F2937] rounded-xl border transition-all duration-300 overflow-hidden cursor-pointer w-full focus:outline-none ${
                  isActive
                    ? "border-[#7C3AED]/60 -translate-y-1"
                    : "border-[#374151]/50 hover:border-[#7C3AED]/40 hover:-translate-y-0.5"
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Top color stripe */}
                <div
                  className="h-1 w-full transition-all duration-300"
                  style={{
                    backgroundColor: isActive ? item.color : "transparent",
                    boxShadow: isActive ? `0 0 12px ${item.color}80` : "none",
                  }}
                />

                <div className="p-6 md:p-7">
                  {/* Index number */}
                  <div
                    className="text-5xl font-black leading-none mb-4 transition-all duration-300 select-none"
                    style={{
                      color: isActive ? item.color : "#374151",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-[#F8FAFC] mb-3 group-hover:text-white transition-colors">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-[#94A3B8] leading-relaxed">
                    {item.description}
                  </p>

                  {/* Expanded detail stat */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isActive ? "max-h-24 mt-5 opacity-100" : "max-h-0 mt-0 opacity-0"
                    }`}
                  >
                    <div
                      className="rounded-lg p-4 border"
                      style={{
                        backgroundColor: `${item.color}10`,
                        borderColor: `${item.color}30`,
                      }}
                    >
                      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: item.color }}>
                        {stat.label}
                      </div>
                      <div className="text-base font-bold text-[#F8FAFC]">{stat.value}</div>
                      <div className="text-xs text-[#64748B] mt-0.5">{stat.sub}</div>
                    </div>
                  </div>

                  {/* Click hint */}
                  <div className={`flex items-center gap-1 mt-4 text-[11px] transition-colors duration-200 ${isActive ? "text-[#7C3AED]" : "text-[#374151] group-hover:text-[#64748B]"}`}>
                    <i className={`ri-${isActive ? "subtract" : "add"}-line text-xs`}></i>
                    <span>{isActive ? "Collapse" : "See details"}</span>
                  </div>
                </div>

                {/* Background glow on active */}
                {isActive && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at 10% 10%, ${item.color}08, transparent 60%)`,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}